import React, { useState, useEffect } from 'react';

import styles from '../../styles/PantallaDeProgramasAcademicos.v2.module.css';
import Pagination from '../../components/Pagination.tsx';
import { usePermissions } from '../../hooks/usePermissions';


const ProgramsTable = (props) => {

  const { query = '', programs: programsProp, loading = false } = props || {};
  // La lista ya viene filtrada desde el backend usando el parámetro `search`.
  const source = Array.isArray(programsProp) ? programsProp : [];
  const filtered = source;

  // PERMISOS
  const { canEdit, canDelete } = usePermissions();
  const puedeEditar = canEdit('Gestión Escolar');
  const puedeEliminar = canDelete('Gestión Escolar');

  // Debug temporal de permisos (quitar en producción si no se necesita)
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.log('[ProgramsTable] permisos Gestión Escolar:', {
      puedeEditar,
      puedeEliminar,
    });
  }


  // PAGINATION GLOBAL
  const [page, setPage] = useState(1);
  const pageSize = 8;

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paginated = filtered.slice((page - 1) * pageSize, page * pageSize);
  const isSearching = (query || '').toString().trim().length > 0;

  // Resetear a la primera página solo cuando cambia el término de búsqueda,
  // no cada vez que cambia la lista (para no perder la página actual al editar).
  useEffect(() => {
    setPage(1);
  }, [query]);

  // Si al cambiar los datos la página actual queda fuera de rango,
  // ajustar a la última página disponible (por ejemplo, después de eliminar).
  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const ActionButtons = ({ onView, onEdit, onDelete }) => (
    <div className={styles.actionButtons}>
      <button aria-label="Ver detalles" className={styles.actionButton} onClick={onView}>
        <img
          src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/a1782ac5b190a9d29d8f798383eedda3f294d56b?placeholderIfAbsent=true"
          alt="Ver"
          className={styles.actionIcon}
        />
      </button>
      {puedeEditar && (
        <button aria-label="Editar" className={styles.actionButton} onClick={onEdit}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/83eacec9f1f974b33615bbc95aea7ff7b2d13b0e?placeholderIfAbsent=true"
            alt="Editar"
            className={styles.actionIcon}
          />
        </button>
      )}
      {puedeEliminar && (
        <button aria-label="Eliminar" className={styles.actionButton} onClick={onDelete}>
          <img
            src="https://api.builder.io/api/v1/image/assets/189f954e013f4fa084ccafbef336b287/bef20ed4bd5293db48e37df8e511dca0fed7db2e?placeholderIfAbsent=true"
            alt="Eliminar"
            className={styles.actionIcon}
          />
        </button>
      )}
      {!puedeEditar && !puedeEliminar && (
        <span style={{ color: '#888', fontSize: '12px' }}>Solo lectura</span>
      )}
    </div>
  );

  const Header = () => (
    <div className={styles.tableHeader}>
      <div className={styles.tableTitle}>
        <div className={styles.tableTitleIcon}>
          <div className={styles.iconContainerOrange}>
            <span>📝</span>
          </div>
        </div>
        <h2 className={styles.tableTitleText}>Programas Académicos</h2>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableScrollWrapper}>
          <Header />
          <div style={{ padding: '20px', textAlign: 'center' }}>Cargando programas...</div>
        </div>
      </div>
    );
  }

  if (!paginated.length) {
    return (
      <div className={styles.tableContainer}>
        <div className={styles.tableScrollWrapper}>
          <Header />
          <div style={{ padding: '20px', textAlign: 'center' }}>
            {isSearching
              ? `Sin resultados para “${query}”.`
              : 'No hay programas académicos registrados.'}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.tableContainer}>
      <div className="table-responsive">
        <div className={styles.tableScrollWrapper}>
          <Header />
          <div className={styles.tableHeaderRow}>
            <span className={styles.headerCell}>Programa</span>
            <span className={styles.headerCell + " d-mobile-none " + styles.headerCellInstitucion}>Institución</span>
            <span className={styles.headerCell}>Sección</span>
            <span className={styles.headerCell + " d-mobile-none"}>Código</span>
            <span className={styles.headerCell + " d-tablet-none"}>Tipo de Periodo</span>
            <span className={styles.headerCell + " d-tablet-none"}>Créditos</span>
            <span className={styles.headerCell}>Estado</span>
            <span className={styles.headerCell}>Acciones</span>
          </div>
          <div className={styles.tableBody}>
            {paginated.length === 0 ? (
              <div className={styles.tableRow}>
                <div className={styles.programInfo}>
                  <span className={styles.programName}>{isSearching ? `Sin resultados para "${query}".` : 'No hay programas académicos registrados.'}</span>
                </div>
                <span className={styles.cellData}>—</span>
                <span className={styles.cellData}>—</span>
                <span className={styles.cellData}>—</span>
                <span className={styles.cellData}>—</span>
                <span className={styles.cellData}>—</span>
                <div className={styles.statusCell}>
                  <div className={styles.statusInactive}><span>—</span></div>
                </div>
                <div className={styles.actionButtons} />
              </div>
            ) : (
              paginated.map((program, index) => (
                <div key={index} className={styles.tableRow}>
                  <div className={styles.programInfo}>
                    <span className={styles.programName}>
                      {(program.name ? String(program.name) : '').split('\n').map((line, i, arr) => (
                        <React.Fragment key={i}>
                          {line}
                          {i < arr.length - 1 && <br />}
                        </React.Fragment>
                      ))}
                    </span>
                  </div>
                  <span className={styles.cellData + " d-mobile-none " + styles.cellInstitucion}>{program.institutionName || program.campus || '—'}</span>
                  <span className={styles.cellData + " d-tablet-none"}>{program.section}</span>
                  <span className={styles.cellData + " d-mobile-none"}>{program.code}</span>
                  <span className={styles.cellData + " d-tablet-none"}>{program.period}</span>
                  <span className={styles.cellData + " d-tablet-none"}>{program.credits}</span>
                  <div className={styles.statusCell}>
                    <div className={program.isActive ? styles.statusActive : styles.statusInactive}>
                      <span>{program.status}</span>
                    </div>
                  </div>
                  <div className={styles.actionButtons}>
                    <ActionButtons
                      onView={() => props?.onView && props.onView(program)}
                      onEdit={() => props?.onEdit && props.onEdit(program)}
                      onDelete={() => props?.onDelete && props.onDelete(program)}
                    />
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
        {/* Paginación global fuera del scroll wrapper */}
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
};

export default React.memo(ProgramsTable);
